// Order recovery — the "Order recovery" panel on the order detail modal.
// Shows what the server did for a booking a provider rejected/cancelled
// (candidates, scores, money, outcome) and gives staff the controls:
// Assign this, Extend window, Suppress, Retry search. Staff only (accessTypes).
// See ORDER_RECOVERY_PLAN.md §8.2 / §8.5.
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ORDER_COLLECTION } from '../../../../config/env';
import { firestore, functions } from '../../../../helpers/Firebase';
import { createNotification } from '../../../../helpers/Utils';
import './ReassignmentPanel.css';

const TZ = 'Asia/Dubai';
const STATE = {
  searching: { tone: 'warning', label: 'Searching for a replacement' },
  running: { tone: 'warning', label: 'Searching for a replacement' },
  candidatesReady: { tone: 'info', label: 'Candidates ready' },
  offered: { tone: 'info', label: 'Waiting for the customer' },
  assigned: { tone: 'success', label: 'Replacement assigned' },
  exhausted: { tone: 'danger', label: 'No replacement found' },
  suppressed: { tone: 'secondary', label: 'Suppressed by staff' },
  failed: { tone: 'danger', label: 'Search failed' },
  abandonedSmoke: { tone: 'secondary', label: 'Abandoned (test)' },
};
const REASON_LABEL = {
  rejectingProvider: 'the provider who rejected', providerInactive: 'provider inactive', providerDeactivated: 'provider deactivated',
  suppressedByAdmin: 'suppressed by staff', failedPreviousAttempt: 'failed a previous attempt',
  recentlyRejectedThisCustomer: 'rejected this customer recently', highCancelRatio: 'high cancel ratio', noActiveWorkers: 'no active workers',
  noEligibleWorker: 'no eligible worker', outOfArea: 'out of area', tooFar: 'too far', noFreeSlot: 'no free slot', priceTooHigh: 'price too high',
  serviceNotOffered: 'service not offered', vehicleCategoryNotOffered: 'vehicle category not offered', washingTypeNotOffered: 'wash type not offered',
  variationNotOffered: 'variation not offered', durationMissing: 'duration missing', bookingHasNoSlot: 'booking has no slot',
};
const MONEY_BRANCH = {
  'cash': 'Cash on completion', 'cash-waived': 'Cash, difference absorbed by Chill', 'same': 'Same price, payment carried over',
  'cheaper': 'Cheaper replacement, difference credited to wallet', 'dearer': 'Dearer replacement, difference absorbed by Chill',
};
const fmt = (ms) => (ms ? moment.tz(Number(ms), TZ).format('D MMM, HH:mm') : '—');
const fmtSlot = (c) => (c && c.slot && c.slot.startMs ? moment.tz(Number(c.slot.startMs), TZ).format('ddd D MMM, h:mm A') : '—');
const aed = (v) => (v == null ? '—' : `AED ${Number(v).toFixed(2)}`);
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const actor = (v) => String(v || '').replace(/^admin:/, '');

const StatePill = ({ state }) => {
  const s = STATE[state] || { tone: 'secondary', label: state };
  return <span className={`rp-state is-${s.tone}`}><i className="rp-dot" />{s.label}</span>;
};

const ReassignmentPanel = ({ booking: bookingProp }) => {
  // The order modal receives a frozen copy of the booking; subscribe to the document so the
  // panel follows the server (offered → assigned / suppressed) while the modal stays open.
  const bookingId = bookingProp && (bookingProp.key || bookingProp.id);
  const wantsLive = !!(bookingProp && (bookingProp.reassignment || bookingProp.reAssignedFrom));
  const [liveDoc, setLiveDoc] = useState(null);
  useEffect(() => {
    if (!bookingId || !wantsLive) return undefined;
    const unsub = firestore().collection(ORDER_COLLECTION).doc(bookingId).onSnapshot(
      (snap) => setLiveDoc(snap.exists ? { ...snap.data(), key: snap.id } : null),
      () => setLiveDoc(null),
    );
    return () => unsub();
  }, [bookingId, wantsLive]);
  const booking = liveDoc || bookingProp;
  const r = booking && booking.reassignment;
  const [busy, setBusy] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [suppressModal, setSuppressModal] = useState(false);
  const [suppressReason, setSuppressReason] = useState('');
  const [, setTick] = useState(0); // 1 s re-render while the customer window is open

  useEffect(() => {
    if (!r || r.state !== 'offered') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [r]);

  const excludedByReason = useMemo(() => {
    const out = {};
    for (const e of (r && r.excluded) || []) {
      const key = e.reason === 'noEligibleWorker'
        ? [...new Set(Object.values(e.workers || {}))].sort().join(' / ') || 'noEligibleWorker'
        : e.reason;
      out[key] = (out[key] || 0) + 1;
    }
    return Object.entries(out).sort((a, b) => b[1] - a[1]);
  }, [r]);

  if (!booking || (!r && !booking.reAssignedFrom)) return null;

  const call = async (name, payload, okMsg) => {
    setBusy(true);
    try {
      const res = await functions().httpsCallable(name)(payload);
      createNotification('success', okMsg || `${name}: ${res.data && res.data.outcome}`, 'Order recovery');
      return res.data;
    } catch (e) {
      createNotification('error', (e && e.message) || String(e), 'Order recovery');
      return null;
    } finally { setBusy(false); }
  };

  // ---------------------------------------------------------------- replacement side
  if (!r && booking.reAssignedFrom) {
    const ro = booking.replacementOf || {};
    return (
      <>
        <div className="border-bottom p-3 mb-5" />
        <div className="rp-card">
          <div className="rp-head">
            <div className="rp-title"><h6>Order recovery</h6><StatePill state="assigned" /></div>
          </div>
          <div className="rp-body">
            <dl className="rp-meta">
              <dt>This booking</dt><dd>Replacement created by order recovery{ro.pickedBy ? <>, picked by <b>{actor(ro.pickedBy)}</b></> : null}.</dd>
              <dt>Replaces</dt><dd>{ro.orderNumber ? <>Order <b>#{ro.orderNumber}</b></> : 'the original order'}{ro.sp_name ? <> with <b>{ro.sp_name}</b></> : null}</dd>
              <dt>Original id</dt><dd className="text-muted">{booking.reAssignedFrom}</dd>
            </dl>
          </div>
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------- original side
  const state = r.state || 'searching';
  const live = booking.reassigning === true;
  const m = r.money || {};
  const secondsLeft = state === 'offered' && r.windowEndsAt ? Math.max(0, Math.round((Number(r.windowEndsAt) - Date.now()) / 1000)) : null;
  const candidates = r.candidates || [];
  const excludedCount = (r.excluded || []).length;
  const canAssign = live && ['candidatesReady', 'offered'].includes(state) && candidates.length > 0;
  const canSuppress = live && !['assigned', 'exhausted', 'suppressed'].includes(state);
  const canExtend = live && state === 'offered';
  const canRetry = live && ['failed', 'candidatesReady', 'searching', 'running'].includes(state);
  const rb = r.rejectedBy || {};
  const rejecter = [rb.sp_name || booking.sp_name, rb.washer_name || booking.washer_name].filter(Boolean).join(' · ');
  const isSearching = state === 'searching' || state === 'running';

  let outcome = null;
  if (state === 'assigned') {
    outcome = (
      <div className="rp-outcome">
        <div>
          <div><b>Replacement created</b>{r.pickedAt ? ` on ${fmt(r.pickedAt)}` : ''}{r.pickedBy ? ` by ${actor(r.pickedBy)}` : ''}.</div>
          <div className="mt-1">{MONEY_BRANCH[m.branch] || (m.branch ? `Money: ${m.branch}` : 'Payment carried over')}
            {m.refundToWallet > 0 ? <>. <b>{aed(m.refundToWallet)}</b> credited to the customer's wallet</> : null}
            {m.waived > 0 ? <>. <b>{aed(m.waived)}</b> absorbed by Chill</> : null}
            {m.prePaid > 0 ? <>. Card payment of <b>{aed(m.prePaid)}</b> carried over</> : null}
            {m.cashDueOnCompletion > 0 ? <>. Cash due on completion <b>{aed(m.cashDueOnCompletion)}</b></> : null}.
          </div>
          {booking.reAssignedTo ? <div className="text-muted mt-1" style={{ fontSize: 12 }}>Replacement booking id: {booking.reAssignedTo}</div> : null}
        </div>
      </div>
    );
  } else if (state === 'exhausted') {
    outcome = <div className="rp-outcome is-danger"><div><b>No replacement could be assigned</b> ({r.exhaustedReason || 'no candidates'}){r.exhaustedAt ? ` on ${fmt(r.exhaustedAt)}` : ''}. The standard cancellation path was applied.</div></div>;
  } else if (state === 'suppressed') {
    outcome = <div className="rp-outcome is-muted"><div><b>Suppressed by {r.suppressedBy || 'staff'}</b>{r.suppressedAt ? ` on ${fmt(r.suppressedAt)}` : ''}{r.suppressedReason ? <> — “{r.suppressedReason}”</> : null}. The standard cancellation path was applied.</div></div>;
  } else if (state === 'failed') {
    outcome = <div className="rp-outcome is-danger"><div><b>The search failed.</b>{r.error ? ` ${r.error}` : ''} Use Retry search to run it again.</div></div>;
  }

  return (
    <>
      <div className="border-bottom p-3 mb-5" />
      <div className="rp-card">
        <div className="rp-head">
          <div className="rp-title">
            <h6>Order recovery</h6>
            <StatePill state={state} />
            {r.attempt > 1 ? <span className="rp-state is-secondary">attempt {r.attempt}</span> : null}
          </div>
          {secondsLeft != null ? <span className={`rp-timer${secondsLeft < 120 ? ' is-low' : ''}`} title="Time left for the customer to pick">{mmss(secondsLeft)} left</span> : null}
        </div>

        <div className="rp-body">
          <dl className="rp-meta">
            <dt>{r.kind === 'canceled' ? 'Cancelled by' : 'Rejected by'}</dt>
            <dd><b>{rejecter || 'Provider'}</b>{rb.reason ? <span className="text-muted"> — “{rb.reason}”</span> : null}</dd>
            <dt>Timeline</dt>
            <dd>Started {fmt(r.startedAt)}{r.searchedAt ? ` · searched ${fmt(r.searchedAt)}` : ''}{r.extendedAt ? ` · window extended ${fmt(r.extendedAt)}` : ''}{r.retriedAt ? ` · retried ${fmt(r.retriedAt)}` : ''}</dd>
          </dl>

          {outcome ? <div className="rp-section">{outcome}</div> : null}

          <div className="rp-section">
            <div className="rp-section-title">
              <span>{isSearching ? 'Looking for candidates…' : `Candidates (${candidates.length})`}</span>
              {r.consideredProviders ? <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>{r.consideredProviders} provider{r.consideredProviders === 1 ? "" : "s"} considered</span> : null}
            </div>
            {candidates.length ? candidates.map((c) => {
              const picked = state === 'assigned' && r.pickedCandidateRank === c.rank;
              const delta = Number(c.slotDeltaMinutes || 0);
              const pd = Number(c.priceDelta || 0);
              const score = Number(c.score || 0);
              return (
                <div key={`${c.sp_id}-${c.washer_id}`} className={`rp-cand${picked ? ' is-picked' : ''}`}>
                  <div className="rp-rank">{c.rank}</div>
                  <div className="rp-cand-main">
                    <div className="rp-cand-name">{c.sp_name} <span>· {c.washer_name}</span>{picked ? <span className="rp-state is-success ml-2">assigned</span> : null}</div>
                    <div className="rp-facts">
                      <span title="Proposed slot"><b>{fmtSlot(c)}</b>{delta ? <span className={delta > 0 ? 'up' : 'down'}> ({delta > 0 ? '+' : ''}{delta} min)</span> : ' (same time)'}</span>
                      <span title={`Serving-area match: ${c.geoMatch}`}>{c.distanceKm != null ? <><b>{c.distanceKm} km</b> away</> : 'in area'}</span>
                      <span title={`Rating source: ${c.ratingSource}`}>{c.rating != null ? <><b>{c.rating} ★</b> ({c.ratingCount})</> : 'unrated'}</span>
                      <span title="Replacement price"><b>{aed(c.price && c.price.totalPrice)}</b>{pd > 0 ? <span className="up"> (+{pd} absorbed)</span> : pd < 0 ? <span className="down"> ({pd} to wallet)</span> : ' (same price)'}</span>
                    </div>
                  </div>
                  <div className="rp-score" title={c.breakdown ? `rating ${c.breakdown.rating} · distance ${c.breakdown.distance} · time ${c.breakdown.time} · price ${c.breakdown.price}` : ''}>
                    <div className="n">{score.toFixed(2)}</div>
                    <div className="l">match</div>
                    <div className="rp-bar"><i style={{ width: `${Math.round(Math.min(1, score) * 100)}%` }} /></div>
                  </div>
                  {canAssign ? <Button color="primary" size="sm" disabled={busy} onClick={() => call('reassignAdminAssign', { bookingId: booking.key || booking.id, rank: c.rank }, `Assigned to ${c.sp_name}`)}>Assign this</Button> : null}
                </div>
              );
            }) : (!isSearching ? <div className="rp-empty">No eligible provider was found for this booking.</div> : null)}

            {excludedCount ? (
              <div className="rp-excluded">
                <button type="button" className="rp-link" onClick={() => setShowExcluded((v) => !v)}>
                  {showExcluded ? 'Hide' : 'Why'} {excludedCount} other provider{excludedCount === 1 ? ' was' : 's were'} excluded{showExcluded ? '' : ' ›'}
                </button>
                {showExcluded ? (
                  <ul>
                    {excludedByReason.map(([reason, n]) => (
                      <li key={reason}>{reason.split(' / ').map((x) => REASON_LABEL[x] || x).join(' / ')} <b>· {n}</b></li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {live ? (
          <div className="rp-foot">
            <span className="hint">Need a provider that is not listed? Use <b>Manage Order</b> below; it creates a linked replacement the same way.</span>
            <div className="rp-actions">
              {canRetry ? <Button size="sm" outline color="secondary" disabled={busy} onClick={() => call('reassignAdminRetry', { bookingId: booking.key || booking.id }, 'Search re-run')}>Retry search</Button> : null}
              {canExtend ? <Button size="sm" outline color="primary" disabled={busy} onClick={() => call('reassignAdminExtend', { bookingId: booking.key || booking.id, minutes: 5 }, 'Window extended by 5 minutes')}>Extend +5 min</Button> : null}
              {canSuppress ? <Button size="sm" outline color="danger" disabled={busy} onClick={() => setSuppressModal(true)}>Suppress</Button> : null}
            </div>
          </div>
        ) : null}
      </div>

      <Modal isOpen={suppressModal} toggle={() => setSuppressModal(false)}>
        <ModalHeader toggle={() => setSuppressModal(false)}>Suppress order recovery</ModalHeader>
        <ModalBody>
          <p>This stops the automatic replacement for this booking and falls back to today's cancellation path: the customer is refunded per the normal rules and gets the standard cancellation message.</p>
          <Label>Reason (required)</Label>
          <Input type="textarea" value={suppressReason} onChange={(e) => setSuppressReason(e.target.value)} placeholder="e.g. customer already rebooked by phone" />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setSuppressModal(false)}>Back</Button>
          <Button color="danger" disabled={busy || suppressReason.trim().length < 3} onClick={async () => {
            const res = await call('reassignAdminSuppress', { bookingId: booking.key || booking.id, reason: suppressReason.trim() }, 'Recovery suppressed');
            if (res) { setSuppressModal(false); setSuppressReason(''); }
          }}>Suppress</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ReassignmentPanel;
